import type { AiStep } from '@beeboard/common';

/**
 * What a saved step of an AI answer means for the page (WI #5767, D4/D5):
 * where the AI's ring sits on the map, which list row / kanban card flashes,
 * what fades out, and which store data to refresh. Pure, so the mapping is
 * testable without stores.
 */

export type AiLiveMode = 'project' | 'template';

/** The run of a project or template, keyed like its chat. */
export function liveKey(mode: AiLiveMode, contextId: string): string {
  return `${mode}:${contextId}`;
}

/** A map entity the AI's ring, glow and follow point at. */
export type AiMapTargetType = 'activity' | 'milestone' | 'phase' | 'workstream';

export interface AiMapTarget {
  type: AiMapTargetType;
  id: string;
}

const MAP_TYPES = new Set<string>([
  'activity',
  'milestone',
  'phase',
  'workstream',
]);

/** Kinds that link an entity to the activity or milestone in `context.parent`. */
const ATTACHED_TO_PARENT = new Set<string>([
  'completion_criterion',
  'approver',
  'activity_document',
  'activity_link',
]);

function parentTarget(step: AiStep): AiMapTarget | null {
  const parent = step.context?.parent;
  if (
    !parent?.id ||
    (parent.type !== 'activity' && parent.type !== 'milestone')
  ) {
    return null;
  }

  return { type: parent.type, id: parent.id };
}

/**
 * The map entity a saved step shows on: the entity itself, or the activity
 * (or milestone) a completion criterion, relation, milestone connection,
 * approver, document or link belongs to. Null for what the map does not
 * show (a function, a member, the document and link catalogs), for a failed
 * step, and for an entity the step removed — that one fades out instead.
 */
export function mapTargetOf(step: AiStep): AiMapTarget | null {
  if (step.status !== 'success' || !step.entity_id) {
    return null;
  }
  const type = step.entity_type;
  if (MAP_TYPES.has(type)) {
    return step.action === 'removed'
      ? null
      : { type: type as AiMapTargetType, id: step.entity_id };
  }
  if (ATTACHED_TO_PARENT.has(type)) {
    return parentTarget(step);
  }
  if (type === 'relation') {
    // The relation is the successor's predecessor; `entity_id` is the
    // predecessor, `context.parent` the successor.
    return parentTarget(step) ?? { type: 'activity', id: step.entity_id };
  }
  if (type === 'milestone_connection') {
    // The activity's milestone relevance changed.
    return { type: 'activity', id: step.entity_id };
  }

  return null;
}

/** The map entities of an answer, each once — for "Auf Karte zeigen". */
export function mapTargetsOf(steps: ReadonlyArray<AiStep> = []): AiMapTarget[] {
  const seen = new Map<string, AiMapTarget>();
  for (const step of steps) {
    const target = mapTargetOf(step);
    if (target) {
      seen.set(`${target.type}:${target.id}`, target);
    }
  }

  return [...seen.values()];
}

/**
 * The list row or kanban card a saved step flashes: a completion criterion
 * has its own row and card, everything else flashes where the map shows it.
 */
export function rowIdOf(step: AiStep): string | null {
  if (
    step.status === 'success' &&
    step.entity_type === 'completion_criterion' &&
    step.action !== 'removed' &&
    step.entity_id
  ) {
    return step.entity_id;
  }

  return mapTargetOf(step)?.id ?? null;
}

/** An entity the step removed from the map: it fades out where it was. */
export function removedTargetOf(step: AiStep): AiMapTarget | null {
  if (
    step.status !== 'success' ||
    step.action !== 'removed' ||
    !step.entity_id ||
    (step.entity_type !== 'activity' && step.entity_type !== 'milestone')
  ) {
    return null;
  }

  return { type: step.entity_type, id: step.entity_id };
}

/**
 * What to reload after a step, so the entity appears, changes or disappears
 * on the map, the list and the kanban board at once.
 */
export interface RefreshPlan {
  /** Activities to fetch one by one (their summary, and cached details). */
  activities: Set<string>;
  /**
   * The whole project / template: what has no endpoint of its own on this
   * page (phases, workstreams, functions, milestones, which also move
   * other entities) or a removal whose side effects the page cannot patch.
   */
  full: boolean;
  /** Projects: the member list. */
  members: boolean;
  /** Templates: the document catalog. */
  documents: boolean;
  /** Templates: the link catalog. */
  links: boolean;
}

export function emptyPlan(): RefreshPlan {
  return {
    activities: new Set(),
    full: false,
    members: false,
    documents: false,
    links: false,
  };
}

/** Puts what `from` still needs into `into` (a batch that failed). */
export function mergePlan(into: RefreshPlan, from: RefreshPlan): void {
  for (const id of from.activities) {
    into.activities.add(id);
  }
  into.full ||= from.full;
  into.members ||= from.members;
  into.documents ||= from.documents;
  into.links ||= from.links;
}

/**
 * Project fields the server carries on to other entities: an Eckpunkt or a
 * criterion that goes underway or done sets its phase and the phase's final
 * milestone underway, a phase change does the same, and a forced deadline
 * removes the relations it violates. A step that sets one of them reloads
 * the whole project.
 */
const PROJECT_CASCADING_FIELDS: Partial<
  Record<AiStep['entity_type'], string[]>
> = {
  activity: ['state', 'status', 'deadline', 'phase'],
  completion_criterion: ['state'],
};

function cascades(mode: AiLiveMode, step: AiStep): boolean {
  const fields =
    mode === 'project' && PROJECT_CASCADING_FIELDS[step.entity_type];

  return (
    !!fields && (step.context?.fields ?? []).some((f) => fields.includes(f))
  );
}

export function isEmptyPlan(plan: RefreshPlan): boolean {
  return (
    !plan.full &&
    !plan.members &&
    !plan.documents &&
    !plan.links &&
    plan.activities.size === 0
  );
}

/** Adds what a saved step needs to the plan; a failed step changed nothing. */
export function addToPlan(
  plan: RefreshPlan,
  mode: AiLiveMode,
  step: AiStep,
): void {
  if (step.status !== 'success' || !step.entity_id) {
    return;
  }
  const parent = step.context?.parent;
  const parentActivity = parent?.type === 'activity' ? parent.id : null;
  if (cascades(mode, step)) {
    plan.full = true;
  }

  switch (step.entity_type) {
    case 'activity':
      if (step.action === 'removed') {
        // Its relations and milestone connections go with it.
        plan.full = true;
      } else {
        plan.activities.add(step.entity_id);
      }
      break;
    case 'completion_criterion':
    case 'activity_document':
    case 'activity_link':
      if (parentActivity) {
        plan.activities.add(parentActivity);
      } else {
        plan.full = true;
      }
      if (step.entity_type === 'activity_document' && mode === 'template') {
        plan.documents = true;
      }
      if (step.entity_type === 'activity_link' && mode === 'template') {
        plan.links = true;
      }
      break;
    case 'relation':
      plan.activities.add(step.entity_id);
      if (parentActivity) {
        plan.activities.add(parentActivity);
      }
      break;
    case 'milestone_connection':
      if (mode === 'template') {
        // The template map derives a milestone's activities from theirs.
        plan.activities.add(step.entity_id);
      } else {
        plan.full = true;
      }
      break;
    case 'approver':
      if (parentActivity) {
        plan.activities.add(parentActivity);
      } else {
        plan.full = true;
      }
      break;
    case 'member':
      plan.members = true;
      break;
    case 'document':
      if (mode === 'template') {
        plan.documents = true;
      }
      break;
    case 'link':
      if (mode === 'template') {
        plan.links = true;
      }
      break;
    default:
      // Milestones, phases, workstreams and functions: no endpoint of their
      // own here, and moving a milestone or phase moves other entities too.
      plan.full = true;
  }
}
